import { BackgroundType, ConfigData, CornerRadiusMode, CountdownPosition } from '@/types';

export type TimerTemplateId =
	| 'standard'
	| 'black_friday'
	| 'cyber_monday'
	| 'holiday_special'
	| 'flash_sale'
	| 'free_shipping';

type TimerTemplatePatch = Partial<
	Pick<
		ConfigData,
		| 'title'
		| 'subtitle'
		| 'font'
		| 'background_type'
		| 'background_color'
		| 'timer_window_color'
		| 'timer_window_border_width'
		| 'timer_window_border_color'
		| 'timer_corner_radius_mode'
		| 'timer_corner_radius_all'
		| 'timer_corner_radius_top_left'
		| 'timer_corner_radius_top_right'
		| 'timer_corner_radius_bottom_left'
		| 'timer_corner_radius_bottom_right'
		| 'countdown_color'
		| 'time_units_color'
		| 'title_font_style'
		| 'subtitle_font_style'
		| 'button_bg_color'
		| 'button_text_color'
	>
>;

export type TimerTemplate = {
	id: TimerTemplateId;
	name: string;
	analyticsName: string;
	patch: TimerTemplatePatch;
	defaultBackgroundName?: string;
};

export const COUNTDOWN_POSITION_OPTIONS = [
	{ label: 'Right of title', value: CountdownPosition.RIGHT_OF_TITLE },
	{ label: 'Left of title', value: CountdownPosition.LEFT_OF_TITLE },
	{ label: 'Above title', value: CountdownPosition.ABOVE_TITLE },
	{ label: 'Below title', value: CountdownPosition.BELOW_TITLE },
] as const;

const DEFAULT_TEMPLATE_CORNER_RADIUS = {
	timer_corner_radius_mode: CornerRadiusMode.ALL,
	timer_corner_radius_all: 0,
	timer_corner_radius_top_left: 0,
	timer_corner_radius_top_right: 0,
	timer_corner_radius_bottom_left: 0,
	timer_corner_radius_bottom_right: 0,
} satisfies Pick<
	ConfigData,
	| 'timer_corner_radius_mode'
	| 'timer_corner_radius_all'
	| 'timer_corner_radius_top_left'
	| 'timer_corner_radius_top_right'
	| 'timer_corner_radius_bottom_left'
	| 'timer_corner_radius_bottom_right'
>;

export const TIMER_TEMPLATES: TimerTemplate[] = [
	{
		id: 'standard',
		name: 'Standard',
		analyticsName: 'Don’t miss out',
		patch: {
			...DEFAULT_TEMPLATE_CORNER_RADIUS,
			title: "Don't miss out!",
			subtitle: 'Last days to grab amazing deals before the sale ends!',
			background_color: '#0C1839',
			timer_window_color: '#172B61',
			countdown_color: '#D1FF53',
			time_units_color: '#A1B2D9',
			title_font_style: '24px bold #4FB0FF',
			subtitle_font_style: '16px normal #A1B2D9',
			button_bg_color: '#D1FF53',
			button_text_color: '16px bold #0C1839',
			font: 'inter',
		},
	},
	{
		id: 'black_friday',
		name: 'Black Friday',
		analyticsName: 'Black Friday sale ends soon',
		defaultBackgroundName: 'Black Friday - 1.png',
		patch: {
			...DEFAULT_TEMPLATE_CORNER_RADIUS,
			title: 'Black Friday sale ends soon',
			subtitle: "Don't miss the biggest deals of the year!",
			background_color: '#161616',
			timer_window_color: '#111111',
			countdown_color: '#FFE817',
			time_units_color: '#A7A0B1',
			title_font_style: '24px bold #FFE817',
			subtitle_font_style: '16px bold #FFFFFF',
			button_bg_color: '#FFE817',
			button_text_color: '16px bold #111111',
			font: 'inter',
		},
	},
	{
		id: 'cyber_monday',
		name: 'Cyber Monday',
		analyticsName: 'Cyber Monday',
		defaultBackgroundName: 'Cyber Monday.png',
		patch: {
			...DEFAULT_TEMPLATE_CORNER_RADIUS,
			title: 'Cyber Monday',
			subtitle: 'Limited-time online deals - shop now!',
			background_color: '#042442',
			timer_window_color: '#042442',
			countdown_color: '#55FFF3',
			time_units_color: '#00A99A',
			title_font_style: '24px bold #55FFF3',
			subtitle_font_style: '16px normal #FFFFFF',
			button_bg_color: '#55FFF3',
			button_text_color: '16px bold #042442',
			font: 'aldrich',
		},
	},
	{
		id: 'holiday_special',
		name: 'Holiday Special',
		analyticsName: 'Holiday special offers',
		defaultBackgroundName: 'Spring.png',
		patch: {
			...DEFAULT_TEMPLATE_CORNER_RADIUS,
			title: 'Holiday special offers',
			subtitle: 'Celebrate with exclusive seasonal discounts',
			background_color: '#F2D5E8',
			timer_window_color: '#11142A',
			countdown_color: '#FFFFFF',
			time_units_color: '#11142A',
			title_font_style: '24px bolditalic #11142A',
			subtitle_font_style: '16px normal #11142A',
			button_bg_color: '#11142A',
			button_text_color: '16px bold #FFFFFF',
			font: 'playfair_display',
		},
	},
	{
		id: 'flash_sale',
		name: 'Flash Sale',
		analyticsName: 'Flash sale live now',
		patch: {
			...DEFAULT_TEMPLATE_CORNER_RADIUS,
			title: 'Flash Sale live now',
			subtitle: 'Grab your favorites before time runs out!',
			background_color: '#B73725',
			timer_window_color: '#B73725',
			countdown_color: '#FFF083',
			time_units_color: '#FFF28C',
			title_font_style: '24px bold #FFF083',
			subtitle_font_style: '16px normal #FFFFFF',
			button_bg_color: '#FFF083',
			button_text_color: '16px bold #B73725',
			font: 'roboto',
		},
	},
	{
		id: 'free_shipping',
		name: 'Free Shipping',
		analyticsName: 'Free shipping promo',
		patch: {
			...DEFAULT_TEMPLATE_CORNER_RADIUS,
			title: 'Free Shipping promo',
			subtitle: 'Enjoy free delivery before the timer hits zero',
			background_color: '#B8D5F4',
			timer_window_color: '#FFFFFF',
			countdown_color: '#0037E6',
			time_units_color: '#0029E1',
			title_font_style: '24px bold #000000',
			subtitle_font_style: '16px normal #000000',
			button_bg_color: '#0037E6',
			button_text_color: '16px bold #FFFFFF',
			font: 'lato',
		},
	},
];

export const TEMPLATE_STYLES: Record<TimerTemplateId, string> = {
	standard: '',
	black_friday: `
        #timer .time-unit .number {
            border-radius: 0 !important;
            margin-bottom: 4px !important;
            padding: 0 4px !important;
        }
    `,
	cyber_monday: `
        #timer .time-unit .number {
            border-radius: 0;
        }
        
        #timer .time-unit .label {
        	font-size: 9px;
            text-transform: uppercase;
        }
    `,
	holiday_special: `
        @import url('https://fonts.googleapis.com/css2?family=PT Mono:wght@400;700&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=PT Serif Caption:wght@400;700&display=swap');
    
        #timer .time-unit .number {
            align-items: center;
            border-radius: 999px !important;
            display: flex;
            height: 46px;
            width: 46px !important;
            justify-content: center;
            margin-bottom: 6px !important;
            min-width: 46px;
            padding: 0 !important;
        }
        
        #amasty-countdown-timer .label {
            font-family: 'PT Mono', sans-serif;
            font-size: 11px;
        }
        
        #timer .time-unit .number {
            font-family: 'PT Serif Caption', sans-serif;
        }   
    `,
	flash_sale: `
        #timer .time-unit .number {
            border: 2px solid #FFF083;
            border-radius: 3px !important;
            margin-bottom: 6px !important;
        }
    `,
	free_shipping: `
        #timer {
            background: #FFFFFF;
            border-radius: 24px;
            padding: 10px 22px;
        }

        #timer .time-unit .number {
            border-radius: 0 !important;
            margin-bottom: 0 !important;
            padding: 0 6px !important;
            font-weight: 900;
        }
    `,
};

export function getTemplateStyles(templateId: string | null | undefined): string {
	if (!templateId) {
		return '';
	}

	return TEMPLATE_STYLES[templateId as TimerTemplateId] ?? '';
}

const DEFAULT_TEMPLATE_ID: TimerTemplateId = 'standard';
const DEFAULT_FRONTEND_ORIGIN = 'http://localhost:3000';

export function getTemplateById(templateId: string | undefined): TimerTemplate {
	return TIMER_TEMPLATES.find((template) => template.id === templateId) ?? TIMER_TEMPLATES[0];
}

export function applyTimerTemplate(
	baseConfig: ConfigData,
	templateId: string | undefined,
	countdownPosition: CountdownPosition | string | undefined,
): ConfigData {
	const template = getTemplateById(templateId ?? DEFAULT_TEMPLATE_ID);
	const resolvedCountdownPosition = resolveCountdownPosition(countdownPosition);

	return {
		...baseConfig,
		...template.patch,
		template_id: template.id,
		custom_css: '',
		countdown_position: resolvedCountdownPosition,
		background_type: template.defaultBackgroundName ? BackgroundType.IMAGE : BackgroundType.COLOR,
		background_image: template.defaultBackgroundName ? buildDefaultBackgroundUrl(template.defaultBackgroundName) : null,
		original_image_name: template.defaultBackgroundName ?? null,
		mobile_use_desktop_settings: true,
		mobile_appearance: null,
	};
}

function resolveCountdownPosition(position: CountdownPosition | string | undefined): CountdownPosition {
	return isCountdownPosition(position) ? position : CountdownPosition.RIGHT_OF_TITLE;
}

function isCountdownPosition(position: CountdownPosition | string | undefined): position is CountdownPosition {
	return Object.values(CountdownPosition).includes(position as CountdownPosition);
}

function buildDefaultBackgroundUrl(backgroundName: string): string {
	const frontendOrigin = resolveFrontendOrigin();
	return `${frontendOrigin}/api/public/default_backgrounds/${encodeURIComponent(backgroundName)}`;
}

function resolveFrontendOrigin(): string {
	const configuredOrigin = process.env.NEXT_PUBLIC_FRONTEND_URL;

	if (configuredOrigin) {
		return configuredOrigin.replace(/\/$/, '');
	}

	if (typeof window !== 'undefined' && window.location.origin) {
		return window.location.origin;
	}

	return DEFAULT_FRONTEND_ORIGIN;
}
